import React, { useEffect } from "react";
import { startFlowHydration } from "./components/flowDiagram/hydrateFlowDiagrams";
import FlowDiagramChrome from "./components/flowDiagram/FlowDiagramChrome";
import RichImageLightbox from "./components/RichImageLightbox";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Login from "./login";
import Dashboard from "./Dashboard";
import CompleteProfile from "./CompleteProfile";
import Signup from "./Signup";
import UsersPage from "./UsersPage";
import ResidentProfile from "./ResidentProfile";
import EvaluationDashboard from "./EvaluationDashboard";
import EvaluationBuilder from "./EvaluationBuilder";
import EvaluationTemplateViewer from "./EvaluationTemplateViewer";
import ResidentEvaluations from "./ResidentEvaluations";
import ResidentEvaluationForm from "./ResidentEvaluationForm";
import ResidentDashboard from "./ResidentDashboard";
import CompletedEvaluationView from "./CompletedEvaluationView";
import EvaluationResponseView from "./EvaluationResponseView";
import ProfilePage from "./ProfilePage";
import WorkHourLog from "./WorkHourLog";
import DirectorWorkLogDashboard from "./DirectorWorkLogDashboard";
import ResidentWorkLogDetail from "./ResidentWorkLogDetail";

import ResidentCaseLogPage from "./ResidentCaseLogPage";
import DirectorCaseSummaryPage from "./DirectorCaseSummaryPage";
import DirectorResidentLogView from "./DirectorResidentLogView";
import CalendarPage from "./pages/CalendarPage";
import RotationSchedulerPage from './pages/RotationSchedulerPage';
import WeekendCallSchedulePage from './pages/WeekendCallSchedulePage';
import DailySchedulePage from './pages/DailySchedulePage';
import SurveyResultsPage from './pages/SurveyResultsPage';
import ResidentRotationSchedulerPage from './pages/ResidentRotationSchedulerPage';
import AttendingDashboard from "./AttendingDashboard";
import AdminDashboard from "./AdminDashboard";
import StudentDashboard from "./StudentDashboard";
import ConferenceSchedulerPage from "./pages/ConferenceSchedulerPage";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LandingPage from "./pages/LandingPage";
import NotificationsPage from "./pages/NotificationsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import DocumentationPage from "./pages/DocumentationPage";
import { NotificationsProvider } from "./contexts/NotificationsContext";
import { DelegationsProvider } from "./contexts/DelegationsContext";
import { InstitutionalDataProvider } from "./contexts/InstitutionalDataContext";
import EvaluatorEvaluationForm from "./EvaluatorEvaluationForm";
import ProtectedRoute from "./components/ProtectedRoute";
import PerformanceHistory from './PerformanceHistory';
import SurveyResults from './SurveyResults';
import AttendingEvaluations from "./AttendingEvaluations";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import TemplateEvaluationsPage from "./pages/TemplateEvaluationsPage";
import PublicAttendancePage from "./pages/PublicAttendancePage";
import AttendanceRedirectPage from "./pages/AttendanceRedirectPage";
import PublicCalendarPage from "./pages/PublicCalendarPage";
import PublicQRCodePage from "./pages/PublicQRCodePage";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingFormBuilderPage from "./pages/OnboardingFormBuilderAdvancedPage";
import DocumentCompletionPage from "./pages/DocumentCompletionPage";
import DocumentRequirementCheck from "./components/DocumentRequirementCheck";
import { DocumentRequirementProvider } from "./contexts/DocumentRequirementContext";
import SuperAdminPage from "./pages/SuperAdminPage";
import SuperAdminAccountManagement from "./pages/SuperAdminAccountManagement";
import InstitutionalAdminDashboard from "./pages/InstitutionalAdminDashboard";
import InstitutionalUserProgramManagementPage from "./pages/InstitutionalUserProgramManagementPage";
import InstitutionalSettingsPage from "./pages/InstitutionalSettingsPage";
import InstitutionalAnnouncementsPage from "./pages/InstitutionalAnnouncementsPage";
import InstitutionalEvaluationsPage from "./pages/InstitutionalEvaluationsPage";
import InstitutionalEvaluationBuilderPage from "./pages/InstitutionalEvaluationBuilderPage";
import InstitutionalEvaluationTemplateResultsPage from "./pages/InstitutionalEvaluationTemplateResultsPage";
import InstitutionalEvaluationResponsePage from "./pages/InstitutionalEvaluationResponsePage";
import InstitutionalCalendarPage from "./pages/InstitutionalCalendarPage";
import InstitutionalPaymentsSubscriptionsPage from "./pages/InstitutionalPaymentsSubscriptionsPage";
import ResourcesPage from "./ResourcesPage";
import DiscussionsPage from "./DiscussionsPage";
import DiscussionPostPage from "./DiscussionPostPage";
import DelegationsModal from "./components/DelegationsModal";
import GuidesLayout from "./pages/guides/GuidesLayout";
import GuidesListPage from "./pages/guides/GuidesListPage";
import NotebookManagePage from "./pages/guides/NotebookManagePage";
import TopicEditorPage from "./pages/guides/TopicEditorPage";
import ArticleEditorPage from "./pages/guides/ArticleEditorPage";
import GuideReadPage from "./pages/guides/GuideReadPage";
import FlashcardsListPage from "./pages/guides/flashcards/FlashcardsListPage";
import FlashcardDeckManagePage from "./pages/guides/flashcards/FlashcardDeckManagePage";
import FlashcardSetEditorPage from "./pages/guides/flashcards/FlashcardSetEditorPage";
import FlashcardSetPage from "./pages/guides/flashcards/FlashcardSetPage";
import FlashcardStudyPage from "./pages/guides/flashcards/FlashcardStudyPage";
import GuideSettingsPage from "./pages/guides/GuideSettingsPage";

function App() {
  // Apply theme immediately on app start to prevent white flash
  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    const isDarkMode = storedTheme ? storedTheme === "dark" : true; // default to dark
    
    // Apply theme immediately to prevent flash
    document.body.setAttribute("data-bs-theme", isDarkMode ? "dark" : "light");
  }, []);

  useEffect(() => startFlowHydration(), []);

  return (
    <HelmetProvider>
      <NotificationsProvider>
        <DelegationsProvider>
          <DocumentRequirementProvider>
            <InstitutionalDataProvider>
            <Router>
            <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/complete-profile/:token" element={<CompleteProfile />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
          <Route path="/documentation/:section" element={<DocumentationPage />} />
          <Route path="/attendance/:maskedCode" element={<PublicAttendancePage />} />
          <Route path="/attendance-redirect/:maskedCode" element={<AttendanceRedirectPage />} />
          <Route path="/qr/:maskedCode" element={<PublicQRCodePage />} />
          <Route path="/calendar/:userId" element={<PublicCalendarPage />} />
          <Route path="/document-completion" element={<ProtectedRoute allowedRoles={["resident", "attending", "director", "admin", "student", "administrative assistant"]}><DocumentCompletionPage /></ProtectedRoute>} />
          <Route path="/super/dashboard" element={<ProtectedRoute allowedRoles={["superuser"]}><SuperAdminPage /></ProtectedRoute>} />
          <Route path="/super/accounts" element={<ProtectedRoute allowedRoles={["superuser"]}><SuperAdminAccountManagement /></ProtectedRoute>} />
          <Route path="/institutional-admin/dashboard" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalAdminDashboard /></ProtectedRoute>} />
          <Route path="/institutional-admin/user-program-management" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalUserProgramManagementPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalEvaluationsPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations/new" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalEvaluationBuilderPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations/edit/:id" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalEvaluationBuilderPage isEdit /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations/completed/:templateId" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalEvaluationTemplateResultsPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations/responses/:responseId" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalEvaluationResponsePage /></ProtectedRoute>} />
          <Route path="/institutional-admin/evaluations/survey-results/:surveyId" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><SurveyResultsPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/calendar" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalCalendarPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/payments-subscriptions" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalPaymentsSubscriptionsPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/settings" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalSettingsPage /></ProtectedRoute>} />
          <Route path="/institutional-admin/announcements" element={<ProtectedRoute allowedRoles={["institutional_admin"]}><InstitutionalAnnouncementsPage /></ProtectedRoute>} />

          {/* Protected routes */}
          <Route element={
            <DocumentRequirementCheck>
              <ProtectedRoute allowedRoles={["resident", "attending", "director", "admin", "student", "administrative assistant"]} />
            </DocumentRequirementCheck>
          }>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/director/dashboard" element={<Dashboard />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/rotation-scheduler" element={<RotationSchedulerPage />} />
          <Route path="/rotation-scheduler/weekend-call" element={<WeekendCallSchedulePage />} />
          <Route path="/rotation-scheduler/daily" element={<DailySchedulePage />} />
          <Route path="/resident/:id" element={<ResidentProfile />} />
          <Route path="/evaluations" element={<EvaluationDashboard />} />
          <Route path="/evaluations/create" element={<EvaluationBuilder />} />
          <Route path="/evaluations/view/:id" element={<EvaluationTemplateViewer />} />
          <Route path="/evaluation/fill/:id" element={<ResidentEvaluationForm />} />
          <Route path="/evaluations/edit/:id" element={<EvaluationBuilder isEdit={true} />} />
          <Route path="/evaluations/duplicate/:id" element={<EvaluationBuilder isDuplicate={true} />} />
          <Route path="/evaluation" element={<ResidentEvaluations />} />
          <Route path="/resident/dashboard" element={<ResidentDashboard />} />
          <Route path="/residents/:id/profile" element={<ResidentProfile />} />
          <Route path="/resident/evaluation/fill/:id" element={<ResidentEvaluationForm />} />
          <Route path="/resident/completed-evaluation/:id" element={<CompletedEvaluationView />} />
          <Route path="/resident/evaluation" element={<ResidentEvaluations />} />
          <Route path="/evaluations/response/:id" element={<EvaluationResponseView />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/resident/work-hours" element={<WorkHourLog />} />
          <Route path="/director/work-logs" element={<DirectorWorkLogDashboard />} />
          <Route path="/director/work-log/:residentId/:month/:year" element={<ResidentWorkLogDetail />} />
          <Route path="/resident/case-log-page" element={<ResidentCaseLogPage />} />
          <Route path="/director/case-summary" element={<DirectorCaseSummaryPage />} />
          <Route path="/director/case-logs/:id" element={<DirectorResidentLogView />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/evaluations/survey-results/:surveyId" element={<SurveyResultsPage />} />
            <Route path="/evaluations/:id/complete" element={<ProtectedRoute allowedRoles={["director", "attending"]}><EvaluatorEvaluationForm /></ProtectedRoute>} />
          <Route path="/resident-rotation-scheduler" element={<ResidentRotationSchedulerPage />} />
          <Route path="/attending/dashboard" element={<AttendingDashboard />} />
          <Route path="/attending/evaluations" element={<AttendingEvaluations />} />
          <Route path="/attending/completed-evaluation/:id" element={<CompletedEvaluationView />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/student/dashboard" element={<StudentDashboard />} />
          <Route path="/resident-profile/:id" element={<ResidentProfile />} />
          <Route path="/conference-scheduler" element={<ConferenceSchedulerPage />} />
          {/* Legacy paths mapped to unified conference scheduler for backwards compatibility */}
          <Route path="/resident/conferences" element={<ConferenceSchedulerPage />} />
          <Route path="/attending/conferences" element={<ConferenceSchedulerPage />} />
          <Route path="/performance-history/:residentId/:templateId" element={<PerformanceHistory />} />
          <Route path="/template-evaluations/:templateId" element={<TemplateEvaluationsPage />} />
          <Route path="/survey-results/:id" element={<SurveyResults />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/onboarding/forms/new" element={<OnboardingFormBuilderPage />} />
          <Route path="/onboarding/forms/:formId/edit" element={<OnboardingFormBuilderPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/discussions" element={<DiscussionsPage />} />
          <Route path="/discussions/:id" element={<DiscussionPostPage />} />
          <Route path="/guides" element={<GuidesLayout />}>
            <Route index element={<GuidesListPage mode="mine" />} />
            <Route path="mine" element={<GuidesListPage mode="mine" />} />
            <Route path="discover" element={<GuidesListPage mode="discover" />} />
            <Route path="shared" element={<GuidesListPage mode="shared" />} />
            <Route path="flashcards" element={<FlashcardsListPage />} />
            <Route path="settings" element={<GuideSettingsPage />} />
          </Route>
          <Route path="/guides/notebooks/:id" element={<NotebookManagePage />} />
          <Route path="/guides/notebooks/:id/topics/:topicId/edit" element={<TopicEditorPage />} />
          <Route path="/guides/notebooks/:id/read" element={<GuideReadPage kind="notebook" />} />
          <Route path="/guides/notebooks/:id/read/:topicId" element={<GuideReadPage kind="notebook" />} />
          <Route path="/guides/articles/:id/edit" element={<ArticleEditorPage />} />
          <Route path="/guides/articles/:id/read" element={<GuideReadPage kind="article" />} />
          <Route path="/guides/flashcards/decks/:id" element={<FlashcardDeckManagePage />} />
          <Route path="/guides/flashcards/sets/:id/edit" element={<FlashcardSetEditorPage />} />
          <Route path="/guides/flashcards/sets/:id/study" element={<FlashcardStudyPage />} />
          <Route path="/guides/flashcards/sets/:id" element={<FlashcardSetPage />} />
          </Route>
            </Routes>
            <FlowDiagramChrome />
            <RichImageLightbox />
            <ToastContainer position="top-center" autoClose={3000} />
            </Router>
            <DelegationsModal />
            </InstitutionalDataProvider>
          </DocumentRequirementProvider>
        </DelegationsProvider>
      </NotificationsProvider>
    </HelmetProvider>
  );
}

export default App;
